"use client";

import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import {
  Address,
  type Chain,
  type HttpTransport,
  parseUnits,
  type PrivateKeyAccount,
  type TypedDataParameter,
  type WalletClient,
  zeroAddress,
} from "viem";

import { errorMessages } from "#/config/errorMessages";
import { type ChainId } from "#/config/web3/contractAddresses";
import { useWalletClient } from "#/hooks/useWalletClient";
import { useAccount } from "#/store/wallet/account/account";
import {
  OrderState,
  PostLimitOrder,
  PostLimitOrderRequest,
  PostLimitOrderResponse,
} from "#/types/orders";
import { getPublicClient } from "#/utils/getPublicClient";
import { getSignatureDeadline } from "#/utils/getSignatureDeadline";
import { getDirectionIndex } from "#/utils/marketActionConverter";
import { extractOrderSize } from "#/utils/orderbook.utils";
import { EIP712Domain } from "#/utils/signature";

import { postLimitOrder } from "../kyan.service";
import { defaultApiResponseHandler } from "../kyan.utils";
import { useTradingSession } from "./useTradingSession";

const UserLimitOrder: TypedDataParameter[] = [
  { name: "deadline", type: "uint256" },
  { name: "instrumentName", type: "string" },
  { name: "size", type: "uint256" },
  { name: "price", type: "uint256" },
  { name: "taker", type: "address" },
  { name: "maker", type: "address" },
  { name: "direction", type: "uint8" },
  { name: "isLiquidation", type: "bool" },
  { name: "isPostOnly", type: "bool" },
  { name: "mmp", type: "bool" },
];

const signLimitOrders = async (
  orders: PostLimitOrder[],
  walletClient: WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
): Promise<PostLimitOrderRequest[]> => {
  const account = walletClient.account.address;
  const signature_deadline = getSignatureDeadline();
  let signatures: Address[];
  try {
    signatures = await Promise.all(
      orders.map(async (order) => {
        return await walletClient.signTypedData({
          account,
          domain: EIP712Domain[walletClient.chain.id as ChainId],
          types: { UserLimitOrder },
          primaryType: "UserLimitOrder",
          message: {
            instrumentName: order.instrument_name,
            size: parseUnits(extractOrderSize(order).toString(), 6),
            price: parseUnits(order.price.toString(), 6),
            taker: order.taker ?? zeroAddress,
            maker: order.maker,
            direction: getDirectionIndex(order.direction),
            isLiquidation: order.liquidation,
            isPostOnly: order.post_only,
            mmp: order.mmp,
            deadline: signature_deadline,
          },
        });
      }),
    );
  } catch (err) {
    console.error("Error signing limit orders: ", err);
    if (String(err).includes("chainId should be same as current chainId")) {
      throw new Error(
        `Wrong chainId on signature: ${await walletClient.getChainId()}`,
      );
    }
    if (String(err).includes("UserRejectedRequestError")) {
      throw new Error("Limit order signature rejected");
    }

    throw new Error("Error signing limit orders");
  }
  return orders.map((order, i) => ({
    ...order,
    signature_deadline,
    signature: signatures[i],
  }));
};

const validateLimitOrderSignatures = async (
  orders: PostLimitOrderRequest[],
  chainId: ChainId,
): Promise<boolean[]> => {
  const publicClient = getPublicClient(chainId);

  return Promise.all(
    orders.map((order) => {
      return publicClient.verifyTypedData({
        address: order.maker,
        domain: EIP712Domain[chainId],
        types: { UserLimitOrder },
        primaryType: "UserLimitOrder",
        message: {
          instrumentName: order.instrument_name,
          size: parseUnits(extractOrderSize(order).toString(), 6),
          price: parseUnits(order.price.toString(), 6),
          maker: order.maker,
          taker: order.taker ?? zeroAddress,
          direction: getDirectionIndex(order.direction),
          isLiquidation: order.liquidation,
          isPostOnly: order.post_only,
          mmp: order.mmp,
          deadline: order.signature_deadline,
        },
        signature: order.signature,
      });
    }),
  );
};
export interface SuccessLimitOrder {
  status: string;
  instrument: string;
}

export const usePostLimitOrders = (
  options?: UseMutationOptions<
    Pick<SuccessLimitOrder, "status" | "instrument">,
    Error,
    PostLimitOrder[]
  >,
) => {
  const { account } = useAccount();
  const { walletClient } = useWalletClient();
  const { shouldUseSessionHash } = useTradingSession();

  return useMutation({
    ...options,
    mutationFn: async (orders: PostLimitOrder[]) => {
      if (!walletClient) {
        throw new Error(errorMessages.WALLET_CLIENT_UNAVAILABLE);
      }

      let response: PostLimitOrderResponse;

      if (shouldUseSessionHash) {
        response = await defaultApiResponseHandler({
          serviceName: "Post Limit Order",
          payload: {
            data: orders.map((order) => ({ ...order, maker: account! })),
            includeSessionHash: true,
          },
          service: postLimitOrder,
        });
      } else {
        const signedData = await signLimitOrders(
          orders.map((order) => ({ ...order, maker: account! })),
          walletClient as WalletClient<HttpTransport, Chain, PrivateKeyAccount>,
        );
        const areSigsValid = await validateLimitOrderSignatures(
          signedData,
          walletClient.chain?.id as ChainId,
        );

        response = await defaultApiResponseHandler({
          serviceName: "Post Limit Order",
          payload: { data: signedData },
          service: postLimitOrder,
          validation: areSigsValid,
        });
      }

      const { posted, rejected } = response;
      const isPartialFill = posted.some(
        (order) =>
          order.order_state === OrderState.Open && order.filled_amount > 0,
      );
      const isFullyFill = posted.some(
        (order) => order.order_state === OrderState.Filled,
      );
      const isFilled = isPartialFill || isFullyFill;
      const cancelled = Boolean(rejected.length);

      if (rejected?.[0]?.reason.includes("post only")) {
        throw new Error("Post only violation: Limit price crossed book.");
      }

      if (cancelled) {
        const reason = rejected[0].reason;
        throw new Error(reason);
      }

      const status = isFilled
        ? ` posted and ${isFullyFill ? "fully" : "partially"} filled`
        : "Successfully posted";

      return { status, instrument: orders[0].instrument_name };
    },
  });
};
